import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, AlertTriangle, Lightbulb, Clock,
  GraduationCap, ShieldCheck, FileText, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../seo/breadcrumbs';
import {
  buildLearningResourceSchema,
  buildFaqSchema,
  buildHowToSchema,
} from '../seo/schemas';
import RelatedLinks from './RelatedLinks';

/**
 * <PillarPageTemplate /> — Template premium pour les pages piliers SEO.
 *
 * Structure éditoriale (basée sur le plan utilisateur "10 étapes") :
 *  1. Hero magazine (kicker + Playfair XXL + sous-titre serif)
 *  2. Intro éditoriale claire
 *  3. Conditions d'accès (cards numérotées)
 *  4. Déroulement (HowTo schema auto si steps)
 *  5. Durée + coût (badges info)
 *  6. Erreurs fréquentes
 *  7. Conseils ECSR
 *  8. FAQ (FAQPage schema auto)
 *  9. RelatedLinks (maillage interne)
 *
 * Schémas générés automatiquement :
 *  - BreadcrumbList
 *  - LearningResource (educational)
 *  - HowTo (si steps[])
 *  - FAQPage (si faq[])
 *
 * Props (data) — voir /data/pillars/*.js pour exemples.
 */
export default function PillarPageTemplate({ data }) {
  const {
    path,
    breadcrumb,
    title,
    titleHighlight,
    kicker,
    subtitle,
    seoTitle,
    seoDescription,
    seoKeywords,
    heroImage,
    heroImageAlt,
    intro,
    conditions = [],
    steps = [],
    duration,
    cost,
    timeRequired, // ISO 8601 (ex: 'P5D', 'PT35H')
    erreurs = [],
    conseils = [],
    faq = [],
    sourceLabel,
    sourceUrl,
    primaryCta,
    secondaryCta,
    relatedTopic,
    accentColor = '#cf2031',
  } = data;

  useSeo({
    title: seoTitle || title,
    description: seoDescription || subtitle,
    keywords: seoKeywords || '',
    path,
    image: heroImage,
    preloadImage: heroImage,
    jsonLd: combineJsonLd(
      buildBreadcrumb(breadcrumb || [[title, path]]),
      buildLearningResourceSchema({
        name: title,
        description: seoDescription || subtitle,
        path,
        learningResourceType: 'Course',
        educationalLevel: data.level || 'Beginner',
        about: data.about || 'Code Moto, ETM 2026, permis A1/A2',
      }),
      steps.length > 0
        ? buildHowToSchema({
            name: `Comment ${(data.howToVerb || 'réussir')} ${title.toLowerCase()}`,
            description: data.howToDescription || subtitle,
            totalTime: timeRequired,
            steps: steps.map((s) => ({ name: s.title, text: s.text })),
          })
        : null,
      faq.length > 0 ? buildFaqSchema(faq) : null,
    ),
  });

  return (
    <article
      className="space-y-10 sm:space-y-14 lg:space-y-16"
      data-testid={`pillar-${path.replace(/\//g, '-').slice(1)}`}
    >
      {/* ============================================================
          1) HERO MAGAZINE
          ============================================================ */}
      <section
        className="relative overflow-hidden rounded-2xl bg-card hero-content-fade"
        style={{
          background: heroImage
            ? `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url('${heroImage}') center/cover`
            : `radial-gradient(ellipse 70% 60% at 100% 0%, ${accentColor}1a, transparent 65%), hsl(var(--card))`,
        }}
        data-testid="pillar-hero"
      >
        <div className="px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-20 relative">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: accentColor }}
            />
            <span
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em]"
              style={{ color: accentColor }}
            >
              {kicker}
            </span>
          </div>
          <h1
            className="font-extrabold tracking-tight text-foreground max-w-4xl leading-[0.98]"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(2.2rem, 6vw, 4.5rem)',
            }}
          >
            {title}
            {titleHighlight && (
              <>
                {' '}
                <span style={{ color: accentColor }} className="italic font-medium">
                  {titleHighlight}
                </span>
              </>
            )}
          </h1>
          <p
            className="text-sm sm:text-base lg:text-lg text-foreground/80 mt-5 max-w-2xl leading-relaxed italic"
            style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
          >
            {subtitle}
          </p>

          {/* Badges info (durée + coût) */}
          {(duration || cost) && (
            <div className="flex flex-wrap gap-2.5 mt-6">
              {duration && (
                <span className="inline-flex items-center gap-2 rounded-full bg-background/40 border border-border/40 px-4 py-2 text-xs font-bold text-foreground/85 backdrop-blur-sm">
                  <Clock className="w-3.5 h-3.5" /> {duration}
                </span>
              )}
              {cost && (
                <span className="inline-flex items-center gap-2 rounded-full bg-background/40 border border-border/40 px-4 py-2 text-xs font-bold text-foreground/85 backdrop-blur-sm">
                  <GraduationCap className="w-3.5 h-3.5" /> {cost}
                </span>
              )}
            </div>
          )}

          {/* CTAs */}
          {(primaryCta || secondaryCta) && (
            <div className="flex gap-2 mt-7 flex-wrap">
              {primaryCta && (
                <Button
                  asChild
                  className="h-11 px-5 text-sm rounded-full font-black shadow-lg text-white"
                  style={{ background: accentColor }}
                  data-testid="pillar-cta-primary"
                >
                  <Link to={primaryCta.to}>
                    {primaryCta.label} <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button
                  asChild
                  variant="outline"
                  className="h-11 px-5 text-sm rounded-full font-bold"
                  data-testid="pillar-cta-secondary"
                >
                  <Link to={secondaryCta.to}>{secondaryCta.label}</Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ============================================================
          2) INTRO ÉDITORIALE
          ============================================================ */}
      {intro && (
        <section className="max-w-3xl mx-auto px-2 sm:px-0" data-reveal="">
          <div
            className="text-base sm:text-lg leading-relaxed text-foreground/85 space-y-4"
            style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            dangerouslySetInnerHTML={{ __html: intro }}
          />
        </section>
      )}

      {/* ============================================================
          3) CONDITIONS D'ACCÈS
          ============================================================ */}
      {conditions.length > 0 && (
        <section data-testid="pillar-conditions" data-reveal="">
          <SectionHeader
            kicker="01 · Conditions d'accès"
            title="Qui peut s'inscrire ?"
            accentColor={accentColor}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {conditions.map((c, i) => (
              <div
                key={i}
                className="rounded-xl bg-card border border-border/40 p-5 hover:border-[hsl(var(--primary))]/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center mb-3 text-white font-black text-sm"
                  style={{ background: accentColor }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3
                  className="font-extrabold text-base leading-tight tracking-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {c.title}
                </h3>
                <p className="text-sm text-foreground/75 mt-2 leading-relaxed">
                  {c.text}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          4) DÉROULEMENT (HowTo)
          ============================================================ */}
      {steps.length > 0 && (
        <section data-testid="pillar-steps" data-reveal="">
          <SectionHeader
            kicker="02 · Déroulement"
            title="Étape par étape"
            accentColor={accentColor}
          />
          <ol className="mt-6 space-y-3" role="list">
            {steps.map((s, i) => (
              <li
                key={i}
                className="rounded-xl bg-card border border-border/40 p-5 sm:p-6 flex gap-5"
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-base"
                  style={{ background: accentColor }}
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className="font-extrabold text-lg sm:text-xl leading-tight tracking-tight"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {s.title}
                  </h3>
                  {s.duration && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.16em] text-foreground/55 mt-1">
                      <Clock className="w-3 h-3" /> {s.duration}
                    </span>
                  )}
                  <p
                    className="text-sm sm:text-base text-foreground/85 mt-2.5 leading-relaxed"
                    style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
                  >
                    {s.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ============================================================
          5) ERREURS FRÉQUENTES
          ============================================================ */}
      {erreurs.length > 0 && (
        <section data-testid="pillar-errors" data-reveal="">
          <SectionHeader
            kicker="03 · À éviter"
            title="Les erreurs qui coûtent"
            accentColor="#b91c1c"
          />
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {erreurs.map((e, i) => (
              <div
                key={i}
                className="rounded-xl bg-card border border-border/40 p-4 flex gap-3"
              >
                <AlertTriangle
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: '#b91c1c' }}
                  aria-hidden="true"
                />
                <div>
                  <h4 className="font-extrabold text-sm tracking-tight">{e.title}</h4>
                  <p className="text-xs sm:text-sm text-foreground/75 mt-1 leading-relaxed">
                    {e.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          6) CONSEILS ECSR
          ============================================================ */}
      {conseils.length > 0 && (
        <section data-testid="pillar-tips" data-reveal="">
          <SectionHeader
            kicker="04 · Conseils ECSR"
            title="Les pros le disent"
            accentColor="#16a34a"
          />
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {conseils.map((c, i) => (
              <div
                key={i}
                className="rounded-xl bg-card border border-border/40 p-4 flex gap-3"
              >
                <Lightbulb
                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                  style={{ color: '#16a34a' }}
                  aria-hidden="true"
                />
                <p className="text-sm text-foreground/85 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          7) FAQ
          ============================================================ */}
      {faq.length > 0 && (
        <section data-testid="pillar-faq" data-reveal="">
          <SectionHeader
            kicker="05 · Questions fréquentes"
            title="On vous répond"
            accentColor={accentColor}
          />
          <div className="mt-6 space-y-2">
            {faq.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl bg-card border border-border/40 overflow-hidden"
              >
                <summary className="flex items-start gap-3 p-5 cursor-pointer list-none hover:bg-background/40 transition-colors">
                  <CheckCircle2
                    className="w-5 h-5 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform"
                    style={{ color: accentColor }}
                    aria-hidden="true"
                  />
                  <span
                    className="font-extrabold text-base sm:text-lg leading-tight tracking-tight"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {f.q}
                  </span>
                </summary>
                <div
                  className="px-5 pb-5 pl-13 text-sm sm:text-base text-foreground/85 leading-relaxed border-t border-border/30 pt-4 mt-1"
                  style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
                >
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          8) SOURCES OFFICIELLES
          ============================================================ */}
      {(sourceLabel || sourceUrl) && (
        <section className="rounded-xl bg-card border border-border/40 p-5 flex items-start gap-3" data-testid="pillar-source">
          <ShieldCheck
            className="w-5 h-5 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]"
            aria-hidden="true"
          />
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-foreground/55 mb-1">
              Sources officielles
            </div>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {sourceLabel}
              {sourceUrl && (
                <>
                  {' · '}
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[hsl(var(--primary))] underline-offset-2 hover:underline font-bold"
                  >
                    Lire <ExternalLink className="w-3 h-3" />
                  </a>
                </>
              )}
            </p>
          </div>
        </section>
      )}

      {/* ============================================================
          9) MAILLAGE INTERNE
          ============================================================ */}
      {relatedTopic && <RelatedLinks topic={relatedTopic} max={5} />}
    </article>
  );
}

function SectionHeader({ kicker, title, accentColor = '#cf2031' }) {
  return (
    <header className="relative">
      <div
        className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] mb-2"
        style={{ color: accentColor }}
      >
        {kicker}
      </div>
      <h2
        className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight leading-tight"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {title}.
      </h2>
    </header>
  );
}
